// Шаблоны тренировок: список, копия, архив.

import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { PageHeader } from '@/components/layout'
import { Badge, Button, Card, EmptyState, ErrorText, PageSpinner, useToast } from '@/components/ui'
import { pluralRu } from '@/lib/format'
import { trainingApi, useInvalidateTraining, useTemplates, type Template } from '@/pages/workouts/api'

function TemplateCard({ template }: { template: Template }) {
  const navigate = useNavigate()
  const toast = useToast()
  const invalidate = useInvalidateTraining()
  const copy = useMutation({
    mutationFn: () => trainingApi.copyTemplate(template.id),
    onSuccess: async (created) => {
      await invalidate()
      navigate(`/templates/${created.id}`)
    },
  })
  const archive = useMutation({
    mutationFn: () => trainingApi.archiveTemplate(template.id, !template.archived),
    onSuccess: async () => {
      await invalidate()
      toast.show({ message: template.archived ? 'Шаблон возвращён из архива' : 'Шаблон в архиве' })
    },
  })
  const count = template.exercises.length
  return (
    <Card
      title={
        <Link to={`/templates/${template.id}`} className="hit-area hover:text-accent">
          {template.name}
        </Link>
      }
      subtitle={`${count} ${pluralRu(count, ['упражнение', 'упражнения', 'упражнений'])}`}
      actions={template.archived ? <Badge tone="warning">архив</Badge> : null}
    >
      <ol className="space-y-1 text-sm text-muted">
        {template.exercises.slice(0, 6).map((e) => (
          <li key={e.id} className="truncate">
            <span className="text-fg">{e.exercise_name}</span>
            {e.target_sets.length ? ` · ${e.target_sets.length} × ${e.target_sets[0]?.reps ?? '—'}` : ''}
          </li>
        ))}
        {count > 6 ? <li>и ещё {count - 6}</li> : null}
      </ol>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button size="sm" onClick={() => navigate(`/templates/${template.id}`)}>
          Изменить
        </Button>
        <Button size="sm" loading={copy.isPending} onClick={() => copy.mutate()}>
          Копировать
        </Button>
        <Button size="sm" variant="ghost" loading={archive.isPending} onClick={() => archive.mutate()}>
          {template.archived ? 'Из архива' : 'В архив'}
        </Button>
      </div>
      <ErrorText error={copy.error ?? archive.error} className="mt-2" />
    </Card>
  )
}

export function TemplatesPage() {
  const [archived, setArchived] = useState(false)
  const templates = useTemplates(archived)
  const navigate = useNavigate()
  return (
    <>
      <PageHeader
        title="Шаблоны"
        description="Упражнения по порядку, целевые подходы, отдых и прогрессия."
        actions={
          <>
            <Button icon="ai" onClick={() => navigate('/ai/plan')}>
              С ИИ
            </Button>
            <Button variant="primary" icon="plus" onClick={() => navigate('/templates/new')}>
              Новый шаблон
            </Button>
            <label className="flex min-h-11 items-center gap-2 text-sm text-fg sm:order-first">
              <input type="checkbox" checked={archived} onChange={(e) => setArchived(e.target.checked)} className="h-4 w-4" />
              Показать архив
            </label>
          </>
        }
      />
      {templates.isPending ? (
        <PageSpinner />
      ) : templates.error ? (
        <ErrorText error={templates.error} />
      ) : templates.data.length === 0 ? (
        <Card>
          <EmptyState
            icon="list"
            title="Шаблонов пока нет"
            description="ИИ составит шаблоны и расписание под цель и инвентарь — проверишь и применишь. Или вручную: «Верх А» — жим лёжа 3 × 8, тяга в наклоне 3 × 10."
            action={
              <div className="flex flex-wrap justify-center gap-2">
                <Button variant="primary" icon="ai" onClick={() => navigate('/ai/plan')}>
                  Составить с ИИ
                </Button>
                <Button icon="plus" onClick={() => navigate('/templates/new')}>
                  Создать вручную
                </Button>
              </div>
            }
          />
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {templates.data.map((t) => (
            <TemplateCard key={t.id} template={t} />
          ))}
        </div>
      )}
    </>
  )
}
